import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State } from "@stencil/core";

import { StzhGhettoboxCloseEvent, StzhGhettoboxOpenEvent } from "../../../index";

import { setPropsIfNull } from "../../../utils/utils";
import { StzhGhettoboxLocalizedText } from "./stzh-ghettobox.localization";

/**
 * @slot - Slot for description content (use instead of description property)
 * @slot button - Slot for button element
 */
@Component({
  tag: "stzh-ghettobox",
  styleUrl: "stzh-ghettobox.scss",
  scoped: true,
})
export class StzhGhettobox {
  /** Overwrite hidden h1 title */
  @Prop() hiddenTitle: string = "";

  /** Main title */
  @Prop() mainTitle: string = "";

  /** Description */
  @Prop() description: string = "";

  /** Hide close button */
  @Prop() hideClose: boolean = false;

  /** Translation strings */
  @Prop() localization: StzhGhettoboxLocalizedText;

  /** Ghettobox open event */
  @Event() stzhOpen: EventEmitter<StzhGhettoboxOpenEvent>;

  /** Ghettobox close event */
  @Event() stzhClose: EventEmitter<StzhGhettoboxCloseEvent>;

  @Element() element: HTMLStzhGhettoboxElement;

  /** Open state */
  @State() open: boolean = true;

  @Method()
  async show() {
    this.open = true;
    this.stzhOpen.emit({
      component: "stzh-ghettobox",
    });
  }

  @Method()
  async hide() {
    this.open = false;
    this.stzhClose.emit({
      component: "stzh-ghettobox",
    });
  }

  private observer: MutationObserver;

  private onCloseButtonClick = () => {
    this.hide();
  };

  private init = () => {
    const button = this.element.querySelector('stzh-button[slot="button"]');

    if (button) {
      setPropsIfNull(button, {
        size: "small",
        sizeMedium: "default",
        variant: "secondary",
        effect: "cta",
        icon: "arrow-right",
        iconPosition: "right",
      } as HTMLStzhButtonElement);
    }
  };

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "ghettobox");
    }
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-ghettobox": true,
      "stzh-ghettobox--has-close-button": !this.hideClose,
    };

    // TODO: Temporary workaround/bugfix for readspeaker class "rs_skip" can be removed once Readspeaker update with bugfix has arrived (ETA end of 2024).
    return (
      <Host hidden={!this.open}>
        <div class={{ ...classes, rs_skip: !this.open }} aria-hidden={this.open ? "false" : "true"}>
          <div class="stzh-ghettobox__container">
            <h1 class="stzh-ghettobox__hidden-title">{this.hiddenTitle || this.localization.hiddenTitle}</h1>
            <div class="stzh-ghettobox__message">
              <div class="stzh-ghettobox__text">
                <h2 class="stzh-ghettobox__main-title">{this.mainTitle}</h2>
                <div class="stzh-ghettobox__description">{this.description ? this.description : <slot></slot>}</div>
              </div>
              <div class="stzh-ghettobox__button">
                <slot name="button"></slot>
              </div>
            </div>
            {!this.hideClose && (
              <stzh-button
                class="stzh-ghettobox__close"
                onClick={this.onCloseButtonClick}
                a11yLabel={this.localization.close}
                variant="tertiary"
                size="small"
                iconOnly={true}
                icon="close-big"
              ></stzh-button>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
